import type { IEdgeRegistration } from '@experiences/interfaces';
import {
    useNavigateWithParams,
    useShowDialog,
} from '@experiences/util';
import { useCallback } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router';
import useSWR from 'swr';

import { routePaths } from '../../common/constants/routePaths';
import {
    deleteEdgeRegistration,
    getEdgeRegistration,
} from '../../services/frp/EdgeRegistrationService';
import { accountGlobalId } from '../../store/selectors';

const useEdgeSettingsViewModel = () => {
    const navigate = useNavigateWithParams();
    const { tenantId = '' } = useParams<{ tenantId: string }>();
    const createDialog = useShowDialog();
    const { formatMessage: translate } = useIntl();

    // Redux state
    const accountId = useSelector(accountGlobalId);

    const {
        data: edgeRegistrationData, isValidating: loading, mutate: edgeMutate,
    } = useSWR(
        (accountId && tenantId) ?
            {
                accountId,
                tenantId,
            } : null,
        getEdgeRegistration,
    );

    const handleAddEdge = useCallback(() => navigate(routePaths.adminTenantEdgeAdd.replace(':tenantId', tenantId)),
        [ navigate, tenantId ]);

    const deleteEdge = useCallback(async (row: IEdgeRegistration) => {
        const proceed = await createDialog({
            title: translate({ id: 'EDGE_DELETE_CONSENT_HEADER' }),
            body: translate({ id: 'EDGE_DELETE_CONSENT_DESCRIPTION' }),
            primaryButtonText: translate({ id: 'CLIENT_CONFIRM' }),
            cancelButtonText: translate({ id: 'CLIENT_CANCEL' }),
            showCancel: true,
            icon: 'warning',
        });
        if (proceed) {
            await deleteEdgeRegistration(accountId, tenantId, row.id);
            edgeMutate();
        }

    }, [ createDialog, translate, accountId, tenantId, edgeMutate ]);

    return {
        accountId,
        tenantId,
        handleAddEdge,
        edgeRegistrationData,
        loading,
        refresh: edgeMutate,
        deleteEdge,
    };
};

export default useEdgeSettingsViewModel;
